import React, { useState, useEffect } from 'react';
import { auth, db, logEvent } from './firebase-config';
import { doc, getDoc, setDoc, writeBatch } from 'firebase/firestore';
import { signInAnonymously } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { hashPassword, generateSalt } from './utils/crypto';
import { requestLoginOptions, loginUser, setRememberDevice as cfSetRememberDevice, clearDeviceSession } from './services/functions';
import { initializeGlobalAudio } from './components/MusicManager';
import { validateUsername } from './utils/usernameValidator';
import './StartScreen.css';

function StartScreen({ onStart, isCrawler = false, onShowIOSInstall, isOnline = true }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [email, setEmail] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const [isNewUser, setIsNewUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [numberOptions, setNumberOptions] = useState([]);
    const [originalNumbers, setOriginalNumbers] = useState([]);
    const [isLocked, setIsLocked] = useState(false);
    const [lockTimeRemaining, setLockTimeRemaining] = useState(0);
    const [previousUsername, setPreviousUsername] = useState(null);
    const [showRememberPrompt, setShowRememberPrompt] = useState(false);
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [showPasswordRecovery, setShowPasswordRecovery] = useState(false);
    const [recoveryMessage, setRecoveryMessage] = useState('');
    const [isRecovering, setIsRecovering] = useState(false);
    
    // Check if iOS device
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone;
    
    // Check for device authentication on mount
    useEffect(() => {
        // Check for pending username from guest conversion
        const pendingUsername = sessionStorage.getItem('pendingUsername');
        if (pendingUsername) {
            const capitalizedUsername = pendingUsername.charAt(0).toUpperCase() + pendingUsername.slice(1);
            setUsername(capitalizedUsername);
            setIsNewUser(true);
            sessionStorage.removeItem('pendingUsername');
        }
        
        // Ensure anonymous auth first
        if (!auth.currentUser) {
            signInAnonymously(auth)
                .then(() => {
                    if (!pendingUsername) {
                        checkDeviceAuth();
                    }
                })
                .catch(err => {
                    console.error('Anonymous auth failed:', err);
                });
        } else {
            if (!pendingUsername) {
                checkDeviceAuth();
            }
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update lock timer
    useEffect(() => {
        if (lockTimeRemaining > 0) {
            const timer = setTimeout(() => {
                setLockTimeRemaining(prev => prev - 1);
                if (lockTimeRemaining === 1) {
                    setIsLocked(false);
                }
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [lockTimeRemaining]);

    const checkDeviceAuth = async () => {
        // First, check localStorage for last played username
        const lastPlayedUsername = localStorage.getItem('lastPlayedUsername') || 'Guest';
        
        if (!auth.currentUser) {
            setUsername(lastPlayedUsername);
            setIsCheckingAuth(false);
            return;
        }
        
        const uid = auth.currentUser.uid;
        console.log('Checking device auth for UID:', uid);
        
        try {
            const deviceAuthDoc = await getDoc(doc(db, 'deviceAuth', uid));
            
            if (deviceAuthDoc.exists()) {
                const data = deviceAuthDoc.data();
                console.log('Device auth data:', data);
                
                if (data.username && data.username !== null) {
                    // User has "remember me" enabled - use their username
                    const capitalizedUsername = data.username.charAt(0).toUpperCase() + data.username.slice(1);
                    console.log('Auto-populating username:', capitalizedUsername);
                    setUsername(capitalizedUsername);
                    setPreviousUsername(data.username); // Keep original for comparison
                    setRememberDevice(true);
                } else {
                    // No "remember me" - use last played username
                    setUsername(lastPlayedUsername);
                }
            } else {
                console.log('No device auth document found');
                // Use last played username from localStorage
                setUsername(lastPlayedUsername);
            }
        } catch (error) {
            console.error('Error checking device auth:', error);
            console.error('Error code:', error.code);
            console.error('Error message:', error.message);
            setUsername(lastPlayedUsername);
        } finally {
            setIsCheckingAuth(false);
        }
    };

    const handleUsernameSubmit = async () => {
        // Initialize audio on first interaction (works on iOS)
        initializeGlobalAudio().catch(err => {
            console.log('🎵 Audio initialization failed:', err);
        });
        const trimmedUsername = username.trim().toLowerCase();
        
        if (!trimmedUsername || trimmedUsername === 'guest') {
            // Play as guest if empty or explicitly "guest"
            localStorage.setItem('lastPlayedUsername', 'Guest');
            // Clear any remembered device session server-side so a stale
            // sessionUsername can't be used to act on an account later.
            if (auth.currentUser) {
                try { await clearDeviceSession(); } catch (err) { console.error('clearDeviceSession failed:', err); }
            }
            onStart('guest', true);
            return;
        }

        // Validate username before proceeding
        const validation = validateUsername(trimmedUsername);
        if (!validation.valid) {
            setError(validation.reason);
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            // Server-side login options: returns 9 numbers including the correct one
            // (generated and shuffled server-side so the correct password number is
            // never sent to the client until a successful loginUser call).
            const result = await requestLoginOptions(trimmedUsername);

            if (!result.exists) {
                // New user - move to signup flow
                setIsNewUser(true);
                return;
            }

            if (result.isLocked) {
                setLockTimeRemaining(result.remainingSeconds);
                setIsLocked(true);
                setError(`Account locked. Try again in ${result.remainingSeconds} seconds.`);
                return;
            }

            // Check if this is the remembered user on the same device
            if (previousUsername && previousUsername === trimmedUsername && rememberDevice) {
                console.log('Remembered user on same device - bypassing password');
                initializeGlobalAudio().catch(err => {
                    console.log('🎵 Audio initialization failed:', err);
                });
                onStart(trimmedUsername, false);
                return;
            }

            if (result.requiresPasswordSetup) {
                // Legacy user with no password set yet
                setIsNewUser(true);
                return;
            }

            // Show login with the server-generated number options
            setIsNewUser(false);
            setOriginalNumbers(result.numbers);
            setNumberOptions([...result.numbers]);
        } catch (err) {
            console.error('Error checking username:', err);
            setError('Error checking username. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasswordSubmit = async (selectedNumber = null) => {
        const trimmedUsername = username.trim().toLowerCase();
        
        // Initialize audio on first interaction (works on iOS)
        initializeGlobalAudio().catch(err => {
            console.log('🎵 Audio initialization failed:', err);
        });
        
        if (isNewUser) {
            // Creating new user or setting password for existing user
            const passwordNum = parseInt(password);
            if (!passwordNum || passwordNum < 1 || passwordNum > 100) {
                setError('Please enter a number between 1 and 100');
                return;
            }

            setIsLoading(true);
            try {
                const salt = generateSalt();
                const passwordHash = await hashPassword(passwordNum, salt);
                
                // Get existing user data if any
                const userDoc = await getDoc(doc(db, 'users', trimmedUsername));
                const existingData = userDoc.exists() ? userDoc.data() : { value: 0, maxCombo: 0 };
                
                // Check for guest score to transfer
                const guestScore = sessionStorage.getItem('guestScore');
                const guestMaxCombo = sessionStorage.getItem('guestMaxCombo');
                const guestMaxCps = sessionStorage.getItem('guestMaxCps');
                if (guestScore) {
                    existingData.value = parseInt(guestScore) || 0;
                    existingData.maxCombo = parseInt(guestMaxCombo) || 0;
                    existingData.maxCps = parseFloat(guestMaxCps) || 0;
                    sessionStorage.removeItem('guestScore');
                    sessionStorage.removeItem('guestMaxCombo');
                    sessionStorage.removeItem('guestMaxCps');
                }
                
                // Use Cloud Function to create user
                const functions = getFunctions(undefined, 'us-central1');
                const createUser = httpsCallable(functions, 'createUser');
                
                await createUser({
                    username: trimmedUsername,
                    passwordNumber: passwordNum,
                    salt,
                    passwordHash,
                    email: email || null,
                    rememberDevice,
                    existingData
                });

                // sessionUsername is now written server-side by createUser CF;
                // no client write needed (and the rule blocks it).

                // Store this username as last played
                localStorage.setItem('lastPlayedUsername', trimmedUsername.charAt(0).toUpperCase() + trimmedUsername.slice(1));

                logEvent('sign_up', { method: 'username', had_guest_score: !!guestScore });

                onStart(trimmedUsername, false);
            } catch (err) {
                console.error('Error creating user:', err);
                console.error('Error details:', err.code, err.message);
                setError(err.message || 'Error creating account. Please try again.');
            } finally {
                setIsLoading(false);
            }
        } else {
            // Logging in existing user
            if (!selectedNumber) {
                setError('Please select your number');
                return;
            }

            setIsLoading(true);
            try {
                // Server verifies the selection, applies lockout, and writes deviceAuth on success.
                // The password hash never leaves the server.
                const result = await loginUser(trimmedUsername, parseInt(selectedNumber), rememberDevice);

                if (result.success) {
                    localStorage.setItem('lastPlayedUsername', trimmedUsername.charAt(0).toUpperCase() + trimmedUsername.slice(1));
                    setShowRememberPrompt(true);
                    setError('');
                } else if (result.isLocked) {
                    setIsLocked(true);
                    setLockTimeRemaining(result.remainingSeconds);
                    setError('Too many failed attempts. Account locked for 15 minutes.');
                } else {
                    setError(`Incorrect password. ${result.attemptsRemaining} attempts remaining.`);
                    // Reshuffle the visible options so the user can't pattern-match positions
                    const shuffled = [...originalNumbers];
                    for (let i = shuffled.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                    }
                    setNumberOptions(shuffled);
                }
            } catch (err) {
                console.error('Error logging in:', err);
                setError(err.message || 'Error logging in. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !isLoading) {
            if (isNewUser === null) {
                handleUsernameSubmit();
            } else if (isNewUser) {
                handlePasswordSubmit();
            }
        }
    };

    const handleBack = () => {
        setIsNewUser(null);
        setPassword('');
        setEmail('');
        setError('');
        setNumberOptions([]);
        setOriginalNumbers([]);
        setShowRememberPrompt(false);
        setShowPasswordRecovery(false);
        setRecoveryMessage('');
    };

    const handlePasswordRecovery = async (e) => {
        e.preventDefault();
        setIsRecovering(true);
        setError('');
        setRecoveryMessage('');

        try {
            // Debug client-side what we're sending
            // eslint-disable-next-line no-console
            console.log('Password recovery - username state:', username, typeof username);
            const trimmed = (username || '').trim().toLowerCase();
            // eslint-disable-next-line no-console
            console.log('Password recovery - trimmed username:', trimmed);
            if (!trimmed) {
                setError('Please enter your username before requesting a reset.');
                return;
            }

            const functions = getFunctions(undefined, 'us-central1');
            const requestPasswordReset = httpsCallable(functions, 'requestPasswordReset');
            
            const result = await requestPasswordReset({ username: trimmed });
            
            if (result.data.success) {
                setRecoveryMessage('If this username exists and has an email address, you will receive a password reset link shortly.');
                setShowPasswordRecovery(false);
                // Clear the form after a delay
                setTimeout(() => {
                    setRecoveryMessage('');
                    handleBack();
                }, 5000);
            }
        } catch (error) {
            console.error('Recovery error:', error);
            if (error.code === 'functions/invalid-argument') {
                setError('Please enter your username before requesting a reset.');
            } else if (error.code === 'functions/resource-exhausted') {
                setError('Too many reset attempts. Please try again later.');
            } else if (error.code === 'functions/not-found') {
                setError('Cloud Functions not yet deployed. The email system is ready but needs deployment.');
            } else {
                setError('Failed to send recovery email. Please try again.');
            }
        } finally {
            setIsRecovering(false);
        }
    };

    const handleRememberDevice = async (remember) => {
        const trimmedUsername = username.trim().toLowerCase();
        setIsLoading(true);

        try {
            // Ensure we have an auth context
            if (!auth.currentUser) {
                await signInAnonymously(auth);
            }

            // sessionUsername was set server-side by loginUser. We only need to
            // update the rememberDevice preference, which goes through a CF that
            // reads sessionUsername from /deviceAuth as the trusted username.
            if (auth.currentUser) {
                await cfSetRememberDevice(remember);
            }
            
            // Store this username as last played
            localStorage.setItem('lastPlayedUsername', trimmedUsername.charAt(0).toUpperCase() + trimmedUsername.slice(1));

            onStart(trimmedUsername, false);
        } catch (err) {
            console.error('Error saving device preference:', err);
            // Provide more specific feedback if possible
            if (err?.code === 'permission-denied') {
                setError('Permission denied saving preference. Please refresh and try again.');
            } else {
                setError('Error saving preference. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Initial username entry
    if (isNewUser === null) {
        return (
            <div className="start-screen-container">
                <div className="start-screen">
                    <h1 className="start-title">
                        Welcome to <br />
                        <span className="click-mania-title">CLICK MANIA</span>
                    </h1>
                    
                    {/* Show offline message */}
                    {!isOnline && (
                        <div className="error-message" style={{ 
                            backgroundColor: '#ff3333', 
                            padding: '15px', 
                            borderRadius: '10px',
                            margin: '20px 0',
                            gridColumn: '1 / -1'
                        }}>
                            ⚠️ No Internet Connection
                            <p style={{ marginTop: '10px', fontSize: '0.9em' }}>
                                Click Mania requires an internet connection to play. 
                                Please check your connection and refresh the page.
                            </p>
                        </div>
                    )}
                    
                    <div className="start-screen-layout">
                        {/* Left side - Game info */}
                        <div className="info-section">
                            <div className="game-description">
                                <p style={{ fontSize: '1.1em', marginBottom: '15px' }}>
                                    <strong>Click Mania</strong> is the ultimate free online multiplayer clicking game where millions of players worldwide compete in real-time!
                                </p>
                                
                                <div className="features-grid">
                                    <div className="feature-item">
                                        <h3>🎯 How to Play</h3>
                                        <ul>
                                            <li>Click or tap as fast as you can</li>
                                            <li>Build epic combo streaks</li>
                                            <li>Watch your clicks per second soar</li>
                                            <li>Compete for the top spot</li>
                                        </ul>
                                    </div>
                                    
                                    <div className="feature-item">
                                        <h3>🏆 Features</h3>
                                        <ul>
                                            <li>Global real-time leaderboards</li>
                                            <li>Weekly competitions</li>
                                            <li>Dynamic music system</li>
                                            <li>Achievement rewards</li>
                                        </ul>
                                    </div>
                                </div>
                                
                                <p style={{ fontSize: '0.95em', marginTop: '15px' }}>
                                    Join the clicking revolution! Whether you're a casual player or a competitive clicker, 
                                    Click Mania offers endless fun with its addictive gameplay, stunning visual effects, 
                                    and global community of players.
                                </p>
                                
                                {isCrawler && (
                                    <div style={{ marginTop: '20px' }}>
                                        <button 
                                            onClick={() => {
                                                // Initialize audio on first interaction
                                                initializeGlobalAudio().catch(err => {
                                                    console.log('🎵 Audio initialization failed:', err);
                                                });
                                                onStart('guest', true);
                                            }} 
                                            className="start-button"
                                            style={{ marginBottom: '10px' }}
                                        >
                                            Play as Guest
                                        </button>
                                        <p>Or create an account to save your progress and compete on leaderboards!</p>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Right side - Play form */}
                        <div className="play-section">
                            <div className="start-form">
                                <p className="play-as-label">Play as:</p>
                                {isCheckingAuth ? (
                                    <div className="auth-loading">
                                        <div className="loading-spinner"></div>
                                        <span>Checking device...</span>
                                    </div>
                                ) : (
                                    <div style={{ position: 'relative', width: '100%' }}>
                                        {username.length >= 20 && (
                                            <p className="character-count" style={{ 
                                                position: 'absolute',
                                                top: '-18px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                fontSize: '0.75em', 
                                                color: '#ff9999',
                                                margin: 0,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                Character limit reached
                                            </p>
                                        )}
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value.length <= 20) {
                                                    setUsername(value);
                                                }
                                            }}
                                            onKeyPress={handleKeyPress}
                                            onFocus={(e) => e.target.select()}
                                            className="username-input"
                                            maxLength={20}
                                            disabled={isLoading || !isOnline}
                                        />
                                    </div>
                                )}
                                {error && <p className="error-message">{error}</p>}
                                <button 
                                    onClick={handleUsernameSubmit} 
                                    className="start-button"
                                    disabled={isLoading || isCheckingAuth || !isOnline}
                                >
                                    {isLoading ? 'Loading...' : 'Play'}
                                </button>
                            
                                {/* iOS Install button - subtle placement */}
                                {isIOS && !isStandalone && onShowIOSInstall && (
                                    <button 
                                        onClick={onShowIOSInstall}
                                        className="ios-install-hint"
                                        style={{
                                            marginTop: '20px',
                                            padding: '8px 16px',
                                            background: 'transparent',
                                            color: 'rgba(255, 255, 255, 0.6)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '4px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.color = 'rgba(255, 255, 255, 0.6)';
                                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                        }}
                                    >
                                        📱 Add to Home Screen
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // New user - password creation
    if (isNewUser) {
        // Check for guest stats to display
        const guestScore = sessionStorage.getItem('guestScore');
        const guestMaxCombo = sessionStorage.getItem('guestMaxCombo');
        const guestMaxCps = sessionStorage.getItem('guestMaxCps');
        
        return (
            <div className="start-screen-container">
                <div className="start-screen centered">
                    <h1 className="start-title">
                        Create Your Account
                    </h1>
                    <p className="start-subtitle">
                        Choose a secret number between 1-100 as your password
                    </p>
                    {guestScore && (
                        <div className="guest-stats-box">
                            <p className="guest-stats-title">Save your current stats!</p>
                            <div className="guest-stats">
                                <span>Clicks: <strong>{parseInt(guestScore).toLocaleString()}</strong></span>
                                <span>Max Combo: <strong>{guestMaxCombo || 0}</strong></span>
                                <span>Max CPS: <strong>{guestMaxCps || '0.0'}</strong></span>
                            </div>
                        </div>
                    )}
                    <div className="start-form">
                        <div className="username-display">
                            Username: <strong>{username}</strong>
                        </div>
                        <input
                            type="number"
                            placeholder="Enter a number (1-100)"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="password-input"
                            min="1"
                            max="100"
                            disabled={isLoading}
                        />
                        <input
                            type="email"
                            placeholder="Email for recovery (optional)"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="email-input"
                            disabled={isLoading}
                        />
                        <label className="remember-device">
                            <input
                                type="checkbox"
                                checked={rememberDevice}
                                onChange={(e) => setRememberDevice(e.target.checked)}
                                disabled={isLoading}
                            />
                            Remember me on this device
                        </label>
                        {error && <p className="error-message">{error}</p>}
                        <div className="button-group">
                            <button 
                                onClick={handleBack} 
                                className="back-button"
                                disabled={isLoading}
                            >
                                Back
                            </button>
                            <button 
                                onClick={() => handlePasswordSubmit()} 
                                className="start-button"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Creating...' : 'Start Playing'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show remember device prompt after successful login
    if (showRememberPrompt) {
        return (
            <div className="start-screen-container">
                <div className="start-screen centered">
                    <h1 className="start-title">
                        Login Successful!
                    </h1>
                    <p className="start-subtitle">
                        Would you like to stay logged in on this device?
                    </p>
                    <div className="start-form">
                        <div className="username-display">
                            Username: <strong>{username}</strong>
                        </div>
                        <p className="remember-explanation">
                            If you select "Yes", you won't need to enter your password next time on this device.
                        </p>
                        {error && <p className="error-message">{error}</p>}
                        <div className="button-group">
                            <button 
                                onClick={() => handleRememberDevice(false)} 
                                className="back-button"
                                disabled={isLoading}
                            >
                                No, ask every time
                            </button>
                            <button 
                                onClick={() => handleRememberDevice(true)} 
                                className="start-button"
                                disabled={isLoading}
                            >
                                Yes, remember me
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Password recovery modal
    if (showPasswordRecovery) {
        return (
            <div className="start-screen-container">
                <div className="start-screen centered">
                    <h1 className="start-title">
                        Password Recovery
                    </h1>
                    <p className="start-subtitle">
                        We'll send a recovery link to your email address
                    </p>
                    <form onSubmit={handlePasswordRecovery} className="start-form">
                        <div className="username-display">
                            Username: <strong>{username}</strong>
                        </div>
                        <p className="recovery-info">
                            If you provided an email when creating your account, we'll send you a link to reset your password.
                        </p>
                        {error && <p className="error-message">{error}</p>}
                        <div className="button-group">
                            <button 
                                type="button"
                                onClick={() => setShowPasswordRecovery(false)} 
                                className="back-button"
                                disabled={isRecovering}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                className="start-button"
                                disabled={isRecovering}
                            >
                                {isRecovering ? 'Sending...' : 'Send Recovery Email'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    // Existing user - login
    return (
        <div className="start-screen-container">
            <div className="start-screen centered">
                <h1 className="start-title">
                    Welcome Back!
                </h1>
                <p className="start-subtitle">
                    Select your secret number to log in
                </p>
                <div className="start-form">
                    <div className="username-display">
                        Username: <strong>{username}</strong>
                    </div>
                    {!isLocked ? (
                        <>
                            <div className="number-options">
                                {numberOptions.map((num) => (
                                    <button
                                        key={num}
                                        onClick={() => handlePasswordSubmit(num)}
                                        className="number-option"
                                        disabled={isLoading}
                                    >
                                        {num}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="lock-message">
                            Account locked. Try again in {lockTimeRemaining} seconds.
                        </div>
                    )}
                    {error && <p className="error-message">{error}</p>}
                    {recoveryMessage && <p className="success-message">{recoveryMessage}</p>}
                    <div className="button-group">
                        <button 
                            onClick={handleBack} 
                            className="back-button"
                            disabled={isLoading}
                        >
                            Back
                        </button>
                        {!isLocked && (
                            <button 
                                onClick={() => setShowPasswordRecovery(true)} 
                                className="link-button"
                                disabled={isLoading}
                            >
                                Forgot Password?
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default StartScreen;